import { Shield, Smartphone, BarChart3, Lock, Workflow, Layers, Clock, Users, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import { CertificationBadgeGroup } from "@/components/CertificationBadge";

const features = [
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "FedRAMP authorized, SOC 2 compliant. Built by cybersecurity experts for maximum protection.",
    color: "primary",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    description: "White-label iOS & Android apps with real-time status, payments, and driver experience.",
    color: "accent",
  },
  {
    icon: BarChart3,
    title: "Analytics",
    description: "Real-time dashboards, custom reports, ROI tracking, and predictive insights.",
    color: "primary",
  },
  {
    icon: Lock,
    title: "OCPP Compliant",
    description: "Full OCPP 1.6 & 2.0.1 support. Hardware-agnostic architecture for flexibility.",
    color: "accent",
  },
  {
    icon: Workflow,
    title: "Load Management",
    description: "Dynamic balancing, peak shaving, and intelligent power distribution.",
    color: "accent",
  },
  {
    icon: Layers,
    title: "API Platform",
    description: "Connect ERP, billing, and telematics through comprehensive REST APIs.",
    color: "primary",
  },
  {
    icon: Users,
    title: "Access Control",
    description: "Driver groups, RFID authentication, fleet cards, and custom pricing.",
    color: "primary",
  },
  {
    icon: Clock,
    title: "24/7 Support",
    description: "NOC monitoring, remote diagnostics, and dedicated success team.",
    color: "accent",
  },
];

export function FeaturesSectionNew() {
  return (
    <section id="technology" className="py-28 relative overflow-hidden bg-card">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-radial opacity-30" />
      
      {/* Section borders */}
      <div className="absolute top-0 left-0 w-full section-divider" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header - More impactful */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mb-20">
          <ScrollReveal>
            <motion.div 
              className="inline-flex items-center gap-2 badge-primary mb-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Platform
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-card-foreground">
              Built for{" "}
              <span className="text-gradient">Scale & Security</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2} className="flex items-end">
            <p className="text-white/70 text-lg lg:text-xl max-w-lg font-light leading-relaxed">
              The most secure and customizable EV charging platform, trusted by enterprises and utilities worldwide.
            </p>
          </ScrollReveal>
        </div>

        {/* Features Grid - Enhanced cards */}
        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" staggerDelay={0.06}>
          {features.map((feature, idx) => (
            <StaggerItem key={idx}>
              <motion.div
                whileHover={{ scale: 1.02, y: -6 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className={`group p-7 rounded-3xl border border-white/15 bg-white/8 backdrop-blur-sm hover:border-${feature.color}/30 hover:bg-white/12 transition-all duration-500 h-full relative overflow-hidden`}
              >
                {/* Hover glow */}
                <motion.div
                  className={`absolute -top-20 -right-20 w-40 h-40 bg-${feature.color}/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                />
                
                <motion.div
                  className={`w-12 h-12 rounded-2xl bg-${feature.color}/10 border border-${feature.color}/20 flex items-center justify-center mb-5 relative z-10`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <feature.icon className={`h-6 w-6 text-${feature.color}`} />
                </motion.div>
                <h3 className="font-semibold text-lg mb-3 relative z-10 text-card-foreground">{feature.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed font-light relative z-10">{feature.description}</p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Compliance Bar - Refined */}
        <ScrollReveal delay={0.4}>
          <div className="mt-20 bg-white/8 border border-white/15 p-8 rounded-3xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-1 text-card-foreground">Enterprise Compliance</h3>
                <p className="text-sm text-white/70">Certified and audited for the highest security standards</p>
              </div>
              <CertificationBadgeGroup 
                badges={["fedramp", "soc2", "iso27001", "ocpi", "openadr", "v2g", "azure"]} 
                size="md" 
              />
            </div>
          </div>
        </ScrollReveal>
      </div>

      <div className="absolute bottom-0 left-0 w-full section-divider" />
    </section>
  );
}